/**
 * Real-time Synchronization Hook
 * Automatically updates user data when admin makes changes
 */
import { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';

export const useRealtimeWalletSync = (userId) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let previousWallet = null;

    try {
      // Listen for wallet changes in real-time
      const walletRef = doc(db, 'wallets', userId);
      
      const unsubscribe = onSnapshot(
        walletRef, 
        (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            
            // Show notification when balance changes
            if (previousWallet && data.balances) {
              const oldUSDT = previousWallet.balances?.USDT || 0;
              const newUSDT = data.balances?.USDT || 0;
              
              if (newUSDT !== oldUSDT) {
                const diff = newUSDT - oldUSDT;
                const message = diff > 0 
                  ? `✅ +$${diff.toFixed(2)} credited to your account!`
                  : `⚠️ -$${Math.abs(diff).toFixed(2)} debited from your account`;
                
                // Show browser notification
                if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                  try {
                    new Notification('Balance Update', { body: message });
                  } catch (e) {
                    console.log('Notification error:', e);
                  }
                }
                
                console.log('💰 Balance updated:', message);
              }
            }
            
            previousWallet = data;
            setWallet(data);
            setLoading(false);
          } else {
            setLoading(false);
          }
        }, 
        (error) => {
          console.error('Error listening to wallet:', error);
          setLoading(false);
        }
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Firebase setup error:', error);
      setLoading(false);
    }
  }, [userId]);

  return { wallet, loading };
};

export const useRealtimeUserSync = (userId) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // Listen for user profile changes
      const userRef = doc(db, 'users', userId);
      
      const unsubscribe = onSnapshot(
        userRef, 
        (doc) => {
          if (doc.exists()) {
            setUserData(doc.data());
            setLoading(false);
          } else {
            setLoading(false);
          }
        },
        (error) => {
          console.error('Error listening to user:', error);
          setLoading(false);
        }
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Firebase setup error:', error);
      setLoading(false);
    }
  }, [userId]);

  return { userData, loading };
};

export const useRealtimeNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!userId) return;

    try {
      // Listen for new notifications
      const notificationsRef = collection(db, 'notifications');
      const q = query(notificationsRef, where('user_id', '==', userId));
      
      const unsubscribe = onSnapshot(
        q, 
        (snapshot) => {
          const newNotifications = [];
          snapshot.forEach((doc) => {
            newNotifications.push({ id: doc.id, ...doc.data() });
          });
          
          // Sort by timestamp
          newNotifications.sort((a, b) => b.timestamp - a.timestamp);
          
          setNotifications(newNotifications);
          
          // Show browser notification for new items (with safety checks)
          if (typeof Notification !== 'undefined') {
            snapshot.docChanges().forEach((change) => {
              if (change.type === 'added') {
                const notificationData = change.doc.data();
                if (Notification.permission === 'granted' && notificationData && notificationData.title) {
                  try {
                    new Notification(notificationData.title, { body: notificationData.message || '' });
                  } catch (error) {
                    console.log('Failed to show notification:', error);
                  }
                }
              }
            });
          }
        },
        (error) => {
          console.error('Error listening to notifications:', error);
        }
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Firebase setup error:', error);
    }
  }, [userId]);

  return { notifications };
};

export const useRealtimeDeposits = (userId) => {
  const [deposits, setDeposits] = useState([]);

  useEffect(() => {
    if (!userId) return;

    try {
      const depositsRef = collection(db, 'deposits');
      const q = query(depositsRef, where('user_id', '==', userId));
      
      const unsubscribe = onSnapshot(
        q, 
        (snapshot) => {
          const depositsList = [];
          snapshot.forEach((doc) => {
            depositsList.push({ id: doc.id, ...doc.data() });
          });
          
          setDeposits(depositsList);
          
          // Notify on status change
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'modified') {
              const deposit = change.doc.data();
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                try {
                  if (deposit.status === 'approved') {
                    new Notification('Deposit Approved! 🎉', {
                      body: `Your deposit of $${deposit.amount} ${(deposit.currency || '').toUpperCase()} has been approved!`
                    });
                  } else if (deposit.status === 'rejected') {
                    new Notification('Deposit Rejected', {
                      body: `Your deposit was rejected. Reason: ${deposit.admin_notes || 'N/A'}`
                    });
                  }
                } catch (e) {
                  console.log('Notification error:', e);
                }
              }
            }
          });
        },
        (error) => {
          console.error('Error listening to deposits:', error);
        }
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Firebase setup error:', error);
    }
  }, [userId]);

  return { deposits };
};
